import type {
  RoomSnapshot, UserRoom, UserFavorite,
  BillingStatus, Plan,
  Post, PostsPage, DigestSettings, KeywordFilter,
  Webhook, WebhookDelivery, Rule, Integration,
  Team, TeamMember,
} from "@tuit/shared-types";
import { userConfigurationStorage } from "./userConfigurationStorage.js";

const memCache = new Map<string, RoomSnapshot>();

export type SnapshotResult =
  | { data: RoomSnapshot; stale: boolean }
  | { data: null };

function isUserRoomId(id: string): boolean {
  return /^u[a-z0-9]{8}$/.test(id);
}

export async function fetchSnapshot(groupId: string): Promise<SnapshotResult> {
  const cached = memCache.get(groupId);
  if (cached) return { data: cached, stale: false };

  const url = isUserRoomId(groupId)
    ? `/api/user-room/${groupId}/snapshot`
    : `/api/room/${groupId}/snapshot`;

  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const snapshot = (await res.json()) as RoomSnapshot;
    memCache.set(groupId, snapshot);
    userConfigurationStorage.snapshotCache.set(groupId, snapshot);
    return { data: snapshot, stale: false };
  } catch {
    const localCached = userConfigurationStorage.snapshotCache.get(groupId);
    if (localCached) {
      memCache.set(groupId, localCached);
      return { data: localCached, stale: true };
    }
    return { data: null };
  }
}

export function invalidateSnapshot(roomId: string): void {
  memCache.delete(roomId);
}

// User rooms

export async function fetchUserRooms(): Promise<UserRoom[]> {
  const res = await fetch("/api/user-rooms");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json() as { rooms: UserRoom[] };
  return data.rooms;
}

export async function createUserRoom(payload: {
  title: string;
  slug: string;
  isPublic: boolean;
}): Promise<UserRoom> {
  const res = await fetch("/api/user-rooms", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<UserRoom>;
}

export async function updateUserRoom(
  id: string,
  patch: Partial<{ title: string; slug: string; isPublic: boolean }>,
): Promise<UserRoom> {
  const res = await fetch(`/api/user-room/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<UserRoom>;
}

export async function deleteUserRoom(id: string): Promise<void> {
  const res = await fetch(`/api/user-room/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function addSourceToRoom(roomId: string, sourceId: string): Promise<void> {
  const res = await fetch(`/api/user-room/${roomId}/sources`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sourceId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
}

export async function removeSourceFromRoom(roomId: string, sourceId: string): Promise<void> {
  const res = await fetch(`/api/user-room/${roomId}/source/${sourceId}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function reorderRoomSources(roomId: string, sourceIds: string[]): Promise<void> {
  const res = await fetch(`/api/user-room/${roomId}/sources`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ sourceIds }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

// Favorites

export async function fetchFavorites(): Promise<UserFavorite[]> {
  const res = await fetch("/api/favorites");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json() as { favorites: UserFavorite[] };
  return data.favorites;
}

export async function addFavorite(roomId: string, roomType: "system" | "user"): Promise<void> {
  const res = await fetch("/api/favorites", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ roomId, roomType }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function removeFavorite(roomId: string): Promise<void> {
  const res = await fetch(`/api/favorite/${roomId}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

// Admin room management

export async function adminListRooms(): Promise<import("@tuit/shared-types").RoomConfig[]> {
  const res = await fetch("/api/admin/rooms");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json() as { rooms: import("@tuit/shared-types").RoomConfig[] };
  return data.rooms;
}

export async function adminUpdateRoomConfig(
  id: string,
  patch: Partial<Pick<import("@tuit/shared-types").RoomConfig, "title" | "refreshIntervalMinutes" | "sourceIds">>,
): Promise<import("@tuit/shared-types").RoomConfig> {
  const res = await fetch(`/api/room/${id}/config`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<import("@tuit/shared-types").RoomConfig>;
}

export async function adminDeleteRoomSnapshot(id: string): Promise<void> {
  const res = await fetch(`/api/room/${id}/snapshot`, { method: "DELETE" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

// Admin source management

export async function adminListSources(): Promise<import("@tuit/shared-types").SourceConfig[]> {
  const res = await fetch("/api/admin/sources");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json() as { sources: import("@tuit/shared-types").SourceConfig[] };
  return data.sources;
}

export async function adminCreateSource(payload: {
  id: string;
  title: string;
  url: string;
  thumbnails: boolean;
  refreshIntervalMinutes: number;
  displayMode?: "text" | "picture" | "gallery";
}): Promise<import("@tuit/shared-types").SourceConfig> {
  const res = await fetch("/api/admin/sources", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<import("@tuit/shared-types").SourceConfig>;
}

export async function adminDeleteSource(id: string): Promise<void> {
  const res = await fetch(`/api/source/${id}/config`, { method: "DELETE" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

// Sources

export async function searchSources(q: string): Promise<Array<{ id: string; title: string; url: string; thumbnails: boolean }>> {
  const res = await fetch(`/api/sources/search?q=${encodeURIComponent(q)}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json() as { sources: Array<{ id: string; title: string; url: string; thumbnails: boolean }> };
  return data.sources;
}

// Billing

export async function getBillingStatus(): Promise<BillingStatus> {
  const res = await fetch("/api/billing/status");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<BillingStatus>;
}

export async function startCheckout(plan: Plan): Promise<string> {
  const res = await fetch("/api/billing/checkout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ plan }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json() as { url: string };
  return data.url;
}

export async function getBillingPortal(): Promise<string> {
  const res = await fetch("/api/billing/portal");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json() as { url: string };
  return data.url;
}

// Posts

export async function getPosts(params: {
  sourceIds?: string[];
  status?: "all" | "unread";
  limit?: number;
  offset?: number;
}): Promise<PostsPage> {
  const q = new URLSearchParams();
  if (params.sourceIds?.length) q.set("sourceIds", params.sourceIds.join(","));
  if (params.status) q.set("status", params.status);
  if (params.limit) q.set("limit", String(params.limit));
  if (params.offset) q.set("offset", String(params.offset));
  const res = await fetch(`/api/posts?${q}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<PostsPage>;
}

export async function markPostsRead(postIds: string[]): Promise<void> {
  const res = await fetch("/api/posts/reads", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ postIds }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function getReadPostIds(): Promise<string[]> {
  const res = await fetch("/api/posts/reads");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json() as { readPostIds: string[] };
  return data.readPostIds;
}

// Search

export async function searchPosts(query: string): Promise<Post[]> {
  const res = await fetch("/api/search", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json() as { results: Post[] };
  return data.results;
}

// Digest

export async function getDigestSettings(): Promise<DigestSettings> {
  const res = await fetch("/api/digest");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<DigestSettings>;
}

export async function updateDigestSettings(patch: Partial<DigestSettings>): Promise<DigestSettings> {
  const res = await fetch("/api/digest", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<DigestSettings>;
}

export async function sendTestDigest(): Promise<void> {
  const res = await fetch("/api/digest/test", { method: "POST" });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
}

// Keyword filters

export async function getFilters(): Promise<KeywordFilter[]> {
  const res = await fetch("/api/filters");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json() as { filters: KeywordFilter[] };
  return data.filters;
}

export async function createFilter(payload: { keyword: string; type: "block" | "highlight" }): Promise<KeywordFilter> {
  const res = await fetch("/api/filters", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<KeywordFilter>;
}

export async function deleteFilter(id: string): Promise<void> {
  const res = await fetch(`/api/filters/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

// Webhooks

export async function getWebhooks(): Promise<Webhook[]> {
  const res = await fetch("/api/webhooks");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json() as { webhooks: Webhook[] };
  return data.webhooks;
}

export async function createWebhook(payload: { url: string; roomScope?: string }): Promise<Webhook> {
  const res = await fetch("/api/webhooks", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<Webhook>;
}

export async function updateWebhook(id: string, patch: { enabled?: boolean; roomScope?: string }): Promise<Webhook> {
  const res = await fetch(`/api/webhooks/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<Webhook>;
}

export async function deleteWebhook(id: string): Promise<void> {
  const res = await fetch(`/api/webhooks/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function getWebhookDeliveries(id: string): Promise<WebhookDelivery[]> {
  const res = await fetch(`/api/webhooks/${id}/deliveries`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<WebhookDelivery[]>;
}

export async function testWebhook(id: string): Promise<void> {
  const res = await fetch(`/api/webhooks/${id}/test`, { method: "POST" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

// Rules

export async function getRules(): Promise<Rule[]> {
  const res = await fetch("/api/rules");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json() as { rules: Rule[] };
  return data.rules;
}

export async function createRule(payload: {
  name: string;
  conditionLogic: "any" | "all";
  conditions: Array<{ field: string; operator: string; value: string }>;
  actions: Array<{ action: string; params?: Record<string, string> }>;
}): Promise<Rule> {
  const res = await fetch("/api/rules", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<Rule>;
}

export async function updateRule(id: string, patch: Partial<{
  name: string; enabled: boolean; conditionLogic: "any" | "all"; roomScope: string;
  conditions: Array<{ field: string; operator: string; value: string }>;
  actions: Array<{ action: string; params?: Record<string, string> }>;
}>): Promise<void> {
  const res = await fetch(`/api/rules/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function deleteRule(id: string): Promise<void> {
  const res = await fetch(`/api/rules/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function testRule(id: string): Promise<{ matchCount: number; testedCount: number; matched: Array<{ id: string; title: string }> }> {
  const res = await fetch(`/api/rules/${id}/test`, { method: "POST" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<{ matchCount: number; testedCount: number; matched: Array<{ id: string; title: string }> }>;
}

// Integrations

export async function getIntegrations(): Promise<Integration[]> {
  const res = await fetch("/api/integrations");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json() as { integrations: Integration[] };
  return data.integrations;
}

export async function createIntegration(payload: { name: string; type: "slack" | "discord" | "teams"; webhookUrl: string }): Promise<Integration> {
  const res = await fetch("/api/integrations", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<Integration>;
}

export async function updateIntegration(id: string, patch: { enabled?: boolean; roomScope?: string; name?: string }): Promise<Integration> {
  const res = await fetch(`/api/integrations/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<Integration>;
}

export async function deleteIntegration(id: string): Promise<void> {
  const res = await fetch(`/api/integrations/${id}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

export async function testIntegration(id: string): Promise<void> {
  const res = await fetch(`/api/integrations/${id}/test`, { method: "POST" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

// Teams

export async function getTeams(): Promise<Team[]> {
  const res = await fetch("/api/teams");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json() as { teams: Team[] };
  return data.teams;
}

export async function createTeam(payload: { name: string }): Promise<Team> {
  const res = await fetch("/api/teams", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<Team>;
}

export async function getTeamMembers(teamId: string): Promise<TeamMember[]> {
  const res = await fetch(`/api/team/${teamId}/members`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<TeamMember[]>;
}

export async function inviteTeamMember(teamId: string, email: string): Promise<void> {
  const res = await fetch(`/api/team/${teamId}/members`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error ?? `HTTP ${res.status}`);
  }
}

export async function removeTeamMember(teamId: string, userId: string): Promise<void> {
  const res = await fetch(`/api/team/${teamId}/members/${userId}`, { method: "DELETE" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
}

// Admin catalog

export async function adminCatalogImport(
  files: Array<{ filename: string; content: string }>,
): Promise<{ inserted: number; skipped: number }> {
  const res = await fetch("/api/admin/catalog/import", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ files }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<{ inserted: number; skipped: number }>;
}

export async function adminCatalogNext(
  excludeIds: string[] = [],
): Promise<{ source: unknown; remaining: number } | null> {
  const params = excludeIds.length ? `?excludeIds=${excludeIds.join(",")}` : "";
  const res = await fetch(`/api/admin/catalog/review/next${params}`);
  if (res.status === 204) return null;
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<{ source: unknown; remaining: number }>;
}

export async function adminCatalogQueue(
  excludeIds: string[] = [],
  limit = 10,
): Promise<{ sources: unknown[]; remaining: number }> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (excludeIds.length) params.set("excludeIds", excludeIds.join(","));
  const res = await fetch(`/api/admin/catalog/review/queue?${params}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<{ sources: unknown[]; remaining: number }>;
}

export async function adminCatalogGet(id: string): Promise<unknown> {
  const res = await fetch(`/api/admin/catalog/sources/${id}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function adminCatalogPatch(
  id: string,
  patch: Record<string, unknown>,
): Promise<unknown> {
  const res = await fetch(`/api/admin/catalog/sources/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

interface FeedResponse {
  feedTitle: string | null;
  feedLink: string | null;
  feedDescription: string | null;
  items: Array<{ title: string; link: string | null; pubDate: string | null; summary: string | null; thumbnail: string | null }>;
}

export async function adminCatalogFeed(url: string): Promise<FeedResponse> {
  const res = await fetch(`/api/admin/catalog/review/feed?url=${encodeURIComponent(url)}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<FeedResponse>;
}

export async function adminCatalogFeedRaw(url: string): Promise<unknown> {
  const res = await fetch(`/api/admin/catalog/review/feed-raw?url=${encodeURIComponent(url)}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export async function adminCatalogFavicon(
  url: string,
): Promise<{ faviconUrl: string | null; title: string | null }> {
  const res = await fetch(`/api/admin/catalog/review/favicon?url=${encodeURIComponent(url)}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json() as Promise<{ faviconUrl: string | null; title: string | null }>;
}
